import ErrorMessage from 'components/common/ErrorMessage';
import { AuthLayout } from 'layouts/AuthLayout';
import React, { memo, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { resetPassword } from 'slices/userSlice';
import { validateResetToken } from 'slices/userSlice';
import { userSelector } from 'slices/userSlice';
import { setError } from 'slices/userSlice';

const Footer = memo(() => (
    <div className='center_wrapper'>
        <p className="para3">Don’t have Interpreter Account? <Link to="/register">Let’s Create One!</Link></p>
    </div>
))

function ResetPassword() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { error , loader } = useSelector(userSelector)
    const {state} = useLocation();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    const token = searchParams.get('token');
    const email = searchParams.get('email');

    const [formValues, setFormValues] = useState({
        email: email || '',
        token: token || '',
        password: '',
        password_confirmation: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [confirmPasswordError, setConfirmPasswordError] = useState("");

    // The link is verified before the password fields are shown, so an expired or
    // already-used link is caught up front instead of after typing a new password.
    const [linkState, setLinkState] = useState('checking'); // checking | valid | invalid
    const [linkError, setLinkError] = useState('');

    useEffect(() => {
        if (!token || !email) {
            setLinkState('invalid');
            setLinkError('This password reset link is invalid or has expired. Please request a new one.');
            return;
        }

        dispatch(validateResetToken({ token, email }, (ok, message) => {
            if (ok) {
                setLinkState('valid');
            } else {
                setLinkState('invalid');
                setLinkError(message);
            }
        }));
    }, [token, email, dispatch]);

    const handleChange = useCallback((e) => {
        setFormValues({ ...formValues, [e.target.name]: e.target.value });
    }, [formValues])

    const submitCallback = (status, message) => {
        if (status) {
            toast.success(message, {
    className:"successToast",
})
            navigate('/login');
        }
        else {
             toast.error(message,
        {
          className:"errorToast",
        }
      );
        }
    };
    const resetAllErrors = () => {
        dispatch(setError({}))
    }
    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        resetAllErrors()
        setConfirmPasswordError("");

        if (formValues.password !== formValues.password_confirmation) {
            setConfirmPasswordError("The passwords do not match.");
            return;
        }

        // token + email travel with the new password so the backend re-validates the
        // link before it changes anything
        dispatch(resetPassword(formValues, submitCallback))
    }, [formValues]);

    
    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };
    
    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };



    if (linkState === 'checking') {
        return (
            <AuthLayout heading="Set New Password" title="Verifying your reset link…" footer={<Footer />}>
                <div className='auth_form' style={{ display: 'flex', justifyContent: 'center', paddingTop: '1.5rem' }}>
                    <Spinner />
                </div>
            </AuthLayout>
        )
    }

    if (linkState === 'invalid') {
        return (
            <AuthLayout heading="Link no longer valid" title={linkError} footer={<Footer />}>
                <div className='auth_form'>
                    <p className="para2">
                        <Link to="/login">
                            <i className="fa-solid fa-chevron-left me-2"></i>
                            {"Back to Login"}
                        </Link>
                    </p>
                    <button type="button" className="btn_auth" onClick={() => navigate('/forgot-password')}>
                        <span>Request a new link</span>
                    </button>
                </div>
            </AuthLayout>
        )
    }

    return (
        <AuthLayout
            heading="Set New Password"
            title=""
            footer={<Footer />}
        >
            <form className='auth_form' action='' onSubmit={handleSubmit}>
                <div className="form-group">
                    <div className="form-group">
                        <div className="row-input">
                            <img src="/images/lock.png" alt="" />
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password" id="password"
                                autoComplete="off"
                                onChange={handleChange}
                                placeholder="Enter a new password"
                                required
                            />
                            <img
                                src={showPassword ? "/images/eye-close.png" : "/images/eye.png"}
                                alt=""
                                onClick={togglePasswordVisibility}
                                style={{ cursor: 'pointer' }}
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <div className="row-input">
                            <img src="/images/lock.png" alt="" />
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                name="password_confirmation"
                                id="password_confirmation"
                                autoComplete="off"
                                placeholder="Enter Confirm password"
                                onChange={handleChange}
                                required
                            />
                            <img
                                src={showConfirmPassword ? "/images/eye-close.png" : "/images/eye.png"}
                                alt=""
                                onClick={toggleConfirmPasswordVisibility}
                                style={{ cursor: 'pointer' }}
                            />
                        </div>
                    </div>
                    {confirmPasswordError && <ErrorMessage text={confirmPasswordError} />}
                    {
                        error?.errors?.password?.map((error) => (
                            <ErrorMessage text={error} />
                        ))
                    }
                </div>
                <button type="submit" disabled={loader} className={`btn_auth ${loader ? "disabled-button" : ""}`}>
                    {loader ? (
                        <>
                        <Spinner size="sm" />
                        <span> Loading </span>
                        </>
                    ) : (
                        <span>Reset Password</span>
                    )}
                </button>
            </form>
        </AuthLayout>
    )

}

export default ResetPassword;
